const ESTIMATING_SITE_HOST = "westernfireproofing.sharepoint.com";
const ESTIMATING_LIBRARY_PATH = "/sites/Estimating/Estimating";
export const ESTIMATING_ALL_ITEMS_URL =
  `https://${ESTIMATING_SITE_HOST}${ESTIMATING_LIBRARY_PATH}/Forms/AllItems.aspx`;
const ESTIMATING_VIEW_ID = "b55658fc-5aed-4a83-a293-f0911d504032";

/**
 * Accept only Estimating-site SharePoint HTTPS URLs (open-redirect protection).
 */
export function sanitizeEstimatingFolderUrl(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const parsed = new URL(url.trim());
    if (parsed.protocol !== "https:") return null;
    if (parsed.hostname.toLowerCase() !== ESTIMATING_SITE_HOST) return null;
    if (!parsed.pathname.toLowerCase().startsWith("/sites/estimating/")) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Build AllItems.aspx deep-link for a project folder name.
 */
export function buildEstimatingFolderViewUrl(folderName) {
  const name = typeof folderName === "string" ? folderName.trim() : "";
  if (!name) return ESTIMATING_ALL_ITEMS_URL;
  const folderPath = `${ESTIMATING_LIBRARY_PATH}/${name}`;
  return `${ESTIMATING_ALL_ITEMS_URL}?id=${encodeURIComponent(folderPath)}&viewid=${ESTIMATING_VIEW_ID}`;
}

/**
 * Best immediate URL without a network round-trip.
 * Prefer Salesforce-stored link, then construct from project name.
 */
export function getImmediateEstimatingFolderUrl({
  estimatingFolderLink,
  projectName,
} = {}) {
  return (
    sanitizeEstimatingFolderUrl(estimatingFolderLink) ||
    (projectName ? buildEstimatingFolderViewUrl(projectName) : null) ||
    ESTIMATING_ALL_ITEMS_URL
  );
}

/**
 * Live resolve via backend (Project Number / item id / name).
 * Falls back to immediate URL on any failure.
 */
export async function resolveEstimatingFolderUrl({
  authFetch,
  projectNumber,
  projectName,
  sharepointItemId,
  estimatingFolderLink,
  signal,
} = {}) {
  const fallback = getImmediateEstimatingFolderUrl({
    estimatingFolderLink,
    projectName,
  });

  if (!authFetch) return fallback;

  const base = (process.env.REACT_APP_BACKEND_BASEURL || "").replace(/\/+$/, "");
  if (!base) return fallback;

  const params = new URLSearchParams();
  if (projectNumber) params.set("projectNumber", String(projectNumber).trim());
  if (projectName) params.set("projectName", String(projectName).trim());
  if (sharepointItemId) params.set("itemId", String(sharepointItemId).trim());
  if (estimatingFolderLink) params.set("storedUrl", String(estimatingFolderLink).trim());

  if (![...params.keys()].length) return fallback;

  try {
    const response = await authFetch(
      `${base}/api/estimating-folders/resolve?${params.toString()}`,
      { method: "GET", signal }
    );
    if (!response.ok) return fallback;
    const data = await response.json();
    return sanitizeEstimatingFolderUrl(data?.url) || fallback;
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return fallback;
  }
}
